import { isFastDay, isUntracked } from "./dayMarks";
import { entriesFor, foodOn } from "./intake";
import type { KayaState } from "./types";

/*
 * "Log dinner?" — one evening nudge, and only when it has something to ask (plan §9 row 5, board
 * YouRemindersDinner). The people it is for forget more than they refuse, so it names the meal and
 * never a number: not what is left of the budget, not what is missing.
 *
 * Pure, so the two paths that deliver it agree: lib/reminders.ts arms a service-worker timer while
 * the app is alive, and the server's push scheduler asks the same question of the synced state.
 * Either one re-asks at the moment it would send — a dinner logged at 20:55 must silence a nudge
 * armed at noon.
 */

export const DINNER_AT_DEFAULT = "21:00";

export interface DinnerSettings {
  on: boolean;
  /** "HH:MM" */
  at: string;
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export function dinnerSettings(S: KayaState): DinnerSettings {
  const p = S.profile;
  const at = p.dinnerRemindAt && HHMM.test(p.dinnerRemindAt) ? p.dinnerRemindAt : DINNER_AT_DEFAULT;
  return { on: p.dinnerRemindOn === true, at };
}

/** Anything eaten logged as dinner on `ds`. A planned dinner not yet logged is not dinner logged. */
export function dinnerLogged(S: KayaState, ds: string): boolean {
  return entriesFor(S, ds).some((e) => e.meal === "dinner");
}

/**
 * Why no nudge goes out on `ds`, or null when it may: switched off, food tracking off, or dinner
 * already logged. (Days marked untracked or a fast join this list when those marks exist.)
 */
export function dinnerQuiet(S: KayaState, ds: string): "off" | "food-off" | "logged" | "untracked" | "fast" | null {
  if (!dinnerSettings(S).on) return "off";
  if (!foodOn(S)) return "food-off";
  if (dinnerLogged(S, ds)) return "logged";
  // A day the person chose not to count, or a fast: nothing to log and nothing to nudge (ADR-049).
  if (isUntracked(S, ds)) return "untracked";
  if (isFastDay(S, ds)) return "fast";
  return null;
}

export interface DinnerNudge {
  /** "HH:MM" on `ds` */
  at: string;
  title: string;
  body: string;
}

/** The nudge for `ds`, or null when it should stay quiet. The time is not checked: the caller
 *  decides whether `at` is still ahead of it. */
export function dinnerNudge(S: KayaState, ds: string): DinnerNudge | null {
  if (dinnerQuiet(S, ds)) return null;
  return { at: dinnerSettings(S).at, title: "Log dinner?", body: "Nothing is logged for dinner yet. Add it while you remember." };
}
